import { Box } from '@chakra-ui/react';
import { IList } from 'core/API/listApi';
import { LocaleKey, useTranslation } from 'helpers/languages/translations';
import { IRetailer } from 'shared/types';
import { MAssortmentStore } from '../MAssortmentStore';
import { MEanPriority } from '../MEanPriority';
import { MEarningPotentials } from '../MEarningPotentials';
import { MLinkage } from '../MLinkage';
import { MNewSecondaryTargetImage } from '../MNewSecondaryTargetImage';
import { MObjectifEMerch } from '../MObjectifEMerch';
import { MObjectifImage } from '../MObjectifImage';
import { MObjectifMAP } from '../MObjectifMAP';
import { MObjectifSearch } from '../MObjectifSearch';
import { MOjectifScore } from '../MOjectifScore';
import { MPdpComplianceTarget } from '../MPdpComplianceTarget';
import { MPdpIndicatorTarget } from '../MPdpIndicatorTarget';
import { MRatingScoreTrershold } from '../MRatingScoreTrershold';
import { RatingAndReviewsThresholds } from '../RatingAndReviewsThresholds';
import { MSearchProduct } from '../MSearchProduct';
import { MSecondaryTargetImage } from '../MSecondaryTargetImage';
import { MTarget } from '../MTarget';
import { MTopPage } from '../MTopPage';
import { MVAT } from '../MVAT';
import { MWeightingBanners } from '../MWeightingBanners';
import { MPdpIndicatorTargetCheck } from '../MPdpIndicatorTargetCheck';
import { MWeightByRetailer } from '../MWeightByRetailer';

export const LinkageHeader = ['main.ean', 'switch.ean', 'type'];
export const MObjectifEMerchHeader = ['criteria', 'weight', 'objectif'];
export const MObjectifPicturesHeader = ['retailer', 'image.number'];
export const MObjectifMAPHeader = ['retailer', 'ean', 'map'];
export const MObjectifScoreHeader = ['retailer', 'category', 'criteria', 'objectif', 'required'];
export const RatingAndReviewsThresholdsHeader = ['retailer', 'category', 'criteria', 'target', 'required'];
export const MRatingScoreHeader = ['retailer', 'category', 'criteria', 'objectif', 'required'];
export const MSecondaryTargetImageHeader = ['retailer', 'image.number', 'check.order'];
export const MNewSecondaryTargetImageHeader = ['retailer', 'treshold.type', 'image.number', 'check.order'];
export const MEanPriorityHeader = ['tag', 'ean', 'order', 'retailer'];
export const MEarningPotentialsHeader = ['shop.id', 'earning.potentials'];
export const MSearchOptimizerHeader = ['ean', 'keywords', 'type'];
export const MPdpIndicatorTargetHeader = [
    'retailer.pdp.indicator',
    'category.pdp.indicator',
    'attribut',
    'weight',
    'unit',
    'min',
    'max',
];
export const MPdpIndicatorTargetCheckHeader = [
    'retailer.pdp.indicator',
    'category.pdp.indicator',
    'indicator',
    'weight',
    'treshold',
];

export const MPdpComplianceTargetHeader = ['retailer', 'objectif.conformity'];
export const MTopPageHeader = ['retailer', 'top'];
export const MWeightingBannersHeader = ['type', 'index'];
export const MSearchProductHeader = ['ean', 'keywords', 'priority'];
export const MAssortmentStoreHeader = ['ean', 'store'];
export const MVATHeader = ['segment', 'vat.rate'];
export const MTargetHeader = ['sales.person', 'retailer', 'status', 'target'];
export const MObjectifSearchHeader = ['product.type', 'target.number', 'target.number.keywords'];
export const MWeightByRetailerHeader = ['retailer', 'weight'];
export const listTypes = {
    1: [
        'linkage',
        'objectif_emerch',
        'objectif_pictures',
        'objectif_mapprice',
        'objectif_ratings_score',
        'objectif_search',
        'top_page',
        'target',
        'secondary_images_target',
        'secondary_images_target_2',
        'weighting_banners',
        'pdp_indicator_target',
        'pdp_compliance_target',
        'potentiel',
        'assortiment_magasins',
        'value_added_tax',
        'rating_score_thresholds',
        'pdp_compliance_check',
        'pdp_content_check_compliance_threshold',
        'pdp_content_check_indicators',
        'weight_by_retailer',
    ],
    2: ['linkage', 'linkage_pdf', 'prioriteean', 'potentiel', 'assortiment_magasins'],
};

export type TListType = typeof listTypes;
export type KeyOflistType = keyof TListType;

export const translateData = (toTranslateArray: string[], prefix = '') => {
    const translate = useTranslation();
    return toTranslateArray.map((toTranslate) => {
        const translatedHeader = translate((prefix + toTranslate) as LocaleKey) as string;
        return translatedHeader !== 'header' ? translatedHeader : toTranslate;
    });
};

export const translateHeaders = (headers: string[]) => {
    const translate = useTranslation();
    return headers.map((header) => {
        const translatedHeader = translate(`list.header.${header}` as LocaleKey) as string;
        return translatedHeader !== 'header' ? translatedHeader : header;
    });
};

export const fetchListData = (
    toolId: number,
    header: string,
    list: IList,
    retailersData: IRetailer[],
    keywordsData: string[],
    categories: {
        id: number;
        name: string;
    }[],
    segments: any[],
    status: string[],
    cats: any[],
) => {
    const retailerName = retailersData.map((ret) => ret.name).sort();
    const categoriesNames = categories.map((cat) => cat.name).sort();

    switch (header) {
        case 'linkage':
            return { headers: LinkageHeader, widget: <MLinkage toolId={toolId} list={list} /> };
        case 'linkage_pdf':
            return { headers: LinkageHeader, widget: <MLinkage toolId={toolId} list={list} /> };
        case 'objectif_emerch':
            return { headers: MObjectifEMerchHeader, widget: <MObjectifEMerch toolId={toolId} list={list} /> };
        case 'objectif_pictures':
            return {
                headers: MObjectifPicturesHeader,
                widget: <MObjectifImage toolId={toolId} retailers={retailerName} list={list} />,
            };
        case 'objectif_mapprice':
            return {
                headers: MObjectifMAPHeader,
                widget: <MObjectifMAP toolId={toolId} retailers={retailerName} list={list} />,
            };
        case 'objectif_ratings_score':
            return {
                headers: MObjectifScoreHeader,
                widget: (
                    <MOjectifScore toolId={toolId} retailers={retailerName} categories={categoriesNames} list={list} />
                ),
            };
        case 'objectif_search':
            return {
                headers: MObjectifScoreHeader,
                widget: <MObjectifSearch toolId={toolId} status={status} list={list} />,
            };
        case 'top_page':
            return {
                headers: MTopPageHeader,
                widget: <MTopPage toolId={toolId} retailers={retailerName} list={list} />,
            };
        case 'target':
            return {
                headers: MTargetHeader,
                widget: <MTarget toolId={toolId} retailers={retailerName} status={status} list={list} />,
            };
        case 'search_product':
            return {
                headers: MSearchProductHeader,
                widget: <MSearchProduct toolId={toolId} list={list} keywords={keywordsData} />,
            };
        case 'prioriteean':
            return {
                headers: MEanPriorityHeader,
                widget: <MEanPriority toolId={toolId} list={list} retailers={retailerName} />,
            };
        case 'assortiment_magasins':
            return { headers: MAssortmentStoreHeader, widget: <MAssortmentStore toolId={toolId} list={list} /> };
        case 'weighting_banners':
            return {
                headers: MWeightingBannersHeader,
                widget: <MWeightingBanners toolId={toolId} list={list} />,
            };
        case 'pdp_indicator_target':
            return {
                headers: MPdpIndicatorTargetHeader,
                widget: (
                    <MPdpIndicatorTarget
                        toolId={toolId}
                        categories={categoriesNames}
                        retailers={retailerName}
                        list={list}
                        retailersData={retailersData}
                    />
                ),
            };


        case 'weight_by_retailer':
            return {
                headers: MWeightByRetailerHeader,
                widget: (
                    <MWeightByRetailer
                        toolId={toolId}
                        retailers={retailersData}
                        list={list}
                    />
                ),
            };


        case 'pdp_content_check_indicators':
            return {
                headers: MPdpIndicatorTargetHeader,
                widget: (
                    <MPdpIndicatorTargetCheck
                        toolId={toolId}
                        categories={categories}
                        retailers={retailersData}
                        list={list}
                    />
                ),
            };
        case 'pdp_compliance_target':
            return {
                headers: MPdpComplianceTargetHeader,
                widget: (
                    <MPdpComplianceTarget toolId={toolId} retailers={retailersData} list={list} allRetailers={false} />
                ),
            };
        case 'pdp_content_check_compliance_threshold':
            return {
                headers: MPdpComplianceTargetHeader,
                widget: (
                    <MPdpComplianceTarget
                        toolId={toolId}
                        retailers={retailersData}
                        list={list}
                        allRetailers={true}
                        useRetailerIds={true}
                        numeric={true}
                    />
                ),
            };
        case 'secondary_images_target':
            return {
                headers: MSecondaryTargetImageHeader,
                widget: <MSecondaryTargetImage toolId={toolId} retailers={retailersData} list={list} />,
            };
        case 'secondary_images_target_2':
            return {
                headers: MNewSecondaryTargetImageHeader,
                widget: <MNewSecondaryTargetImage toolId={toolId} retailers={retailersData} list={list} />,
            };
        case 'potentiel':
            return { headers: MEarningPotentialsHeader, widget: <MEarningPotentials toolId={toolId} list={list} /> };
        case 'value_added_tax':
            return { headers: MVATHeader, widget: <MVAT toolId={toolId} list={list} segments={segments} /> };
        case 'rating_score_thresholds':
            return {
                headers: MRatingScoreHeader,
                widget: (
                    <MRatingScoreTrershold toolId={toolId} retailers={retailersData} categories={cats} list={list} />
                ),
            };
        case 'rating_and_reviews_thresholds':
            return {
                headers: RatingAndReviewsThresholdsHeader,
                widget: (
                    <RatingAndReviewsThresholds
                        toolId={toolId}
                        retailers={retailersData}
                        categories={cats}
                        list={list}
                    />
                ),
            };
        default:
            return { headers: [], widget: <Box>No Template</Box> };
    }
};
